// src/components/InvoiceItemsEditor.jsx
import React, { useState, useMemo } from "react";
import Select from "./ui/Select";
import { validateInvoiceItem } from "../utils/invoiceValidation";
import { toastErrors } from "../utils/toastErrors";
import { toLatinDigits } from "../utils/numberFormat";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 12,
    borderColor: state.isFocused ? "#3b82f6" : "#e5e7eb", // blue-500 / gray-200
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
    ":hover": { borderColor: state.isFocused ? "#3b82f6" : "#d1d5db" },
    backgroundColor: "white",
    direction: "rtl",
    fontFamily: "inherit",
  }),
  valueContainer: (b) => ({ ...b, paddingInline: 12 }),
  placeholder: (b) => ({ ...b, color: "#9ca3af" }),
  singleValue: (b) => ({ ...b, direction: "rtl" }),
  menu: (b) => ({ ...b, zIndex: 50, direction: "rtl" }),
};

const inputClass =
  "w-full rounded-xl border border-gray-200 px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function InvoiceItemsEditor({
  items,
  onItemsChange,
  unitOptions,
  countryOptions,
}) {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [modalItemData, setModalItemData] = useState({});

  // Open / Close modal
  const openEditModal = (index) => {
    setEditingItemIndex(index);
    setModalItemData({ ...items[index] });
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditingItemIndex(null);
    setModalItemData({});
  };

  // Field updaters inside modal
  const handleModalChange = (e) => {
    const { name, value, type, checked } = e.target;
    setModalItemData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  const handleModalUnitSelect = (opt) => {
    setModalItemData((prev) => ({ ...prev, unit: opt ? opt.value : "" }));
  };
  const handleSelectChange = (name, value) => {
    setModalItemData((prev) => ({ ...prev, [name]: value }));
  };

  // Save to parent
  const handleModalSave = () => {
    const problems = validateInvoiceItem(modalItemData);
    if (problems.length) {
      toastErrors(problems);
      return;
    }
    const updated = [...items];
    updated[editingItemIndex] = { ...modalItemData };
    onItemsChange(updated);
    closeModal();
  };

  // Add / Remove
  const addNewItem = () => {
    const newItem = {
      description: "",
      quantity: 1,
      unit_price: 0,
      nw_kg: 0,
      gw_kg: 0,
      unit: "U",
      commodity_code: "",
      pack: 1,
      origin: "",
    };
    onItemsChange([...(items || []), newItem]);
  };
  const removeItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    onItemsChange(updated);
  };

  // Totals
  const totals = useMemo(() => {
    const sub_total = (items || []).reduce(
      (acc, it) => acc + Number(it.quantity || 0) * Number(it.unit_price || 0),
      0
    );
    const total_gw = (items || []).reduce(
      (acc, it) => acc + Number(it.gw_kg || 0),
      0
    );
    const total_nw = (items || []).reduce(
      (acc, it) => acc + Number(it.nw_kg || 0),
      0
    );
    const total_qty = (items || []).reduce(
      (acc, it) => acc + Number(it.quantity || 0),
      0
    );
    const total_pack = (items || []).reduce(
      (acc, it) => acc + Number(it.pack || 0),
      0
    );
    return { sub_total, total_gw, total_nw, total_qty, total_pack };
  }, [items]);

  return (
    <div dir="rtl" className="font-vazir">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xl md:text-2xl font-bold">اقلام فاکتور</h3>
        <button
          type="button"
          onClick={addNewItem}
          className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
        >
          افزودن کالا
        </button>
      </div>

      {/* Table Card */}
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="border-b px-4 md:px-6 py-4">
          <p className="text-sm text-gray-600">لیست اقلام ثبت شده</p>
        </div>

        {items && items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-right">
                  <th className="p-3">شرح کالا</th>
                  <th className="p-3">تعداد</th>
                  <th className="p-3">کد کالا</th>
                  <th className="p-3">قیمت واحد</th>
                  <th className="p-3">واحد</th>
                  <th className="p-3">بسته‌بندی</th>
                  <th className="p-3">وزن ناخالص (کیلوگرم)</th>
                  <th className="p-3">وزن خالص (کیلوگرم)</th>
                  <th className="p-3">مبدأ</th>
                  <th className="p-3">جمع خطی</th>
                  <th className="p-3">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const lineTotal =
                    Number(item.quantity || 0) * Number(item.unit_price || 0);
                  return (
                    <tr
                      key={index}
                      className="border-t hover:bg-gray-50/60 transition"
                    >
                      <td className="p-3">{item.description}</td>
                      <td className="p-3">{item.quantity}</td>
                      <td className="p-3">{item.commodity_code}</td>
                      <td className="p-3">{item.unit_price}</td>
                      <td className="p-3">{item.unit}</td>
                      <td className="p-3">{item.pack}</td>
                      <td className="p-3">{item.gw_kg}</td>
                      <td className="p-3">{item.nw_kg}</td>
                      <td className="p-3">{item.origin}</td>
                      <td className="p-3">{lineTotal.toFixed(2)}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(index)}
                            className="px-3 py-2 rounded-xl border bg-gray-50 hover:bg-gray-100 transition"
                          >
                            ویرایش
                          </button>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="px-3 py-2 rounded-xl border text-red-600 hover:bg-red-50 transition"
                          >
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-10 text-center text-gray-500">
            اقلامی برای نمایش وجود ندارد
          </div>
        )}
      </div>

      {/* Totals Card */}
      <div className="mt-4 rounded-2xl border bg-white shadow-sm">
        <div className="border-b px-4 md:px-6 py-4">
          <h4 className="text-sm font-semibold">مجموعات</h4>
        </div>
        <div className="px-4 md:px-6 py-6 grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-gray-700">جمع فرعی</label>
            <input
              type="text"
              value={totals.sub_total.toFixed(2)}
              readOnly
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-gray-700">
              وزن ناخالص کل (کیلوگرم)
            </label>
            <input
              type="text"
              value={totals.total_gw}
              readOnly
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-gray-700">
              وزن خالص کل (کیلوگرم)
            </label>
            <input
              type="text"
              value={totals.total_nw}
              readOnly
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-gray-700">تعداد کل</label>
            <input
              type="text"
              value={totals.total_qty}
              readOnly
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-gray-700">تعداد بسته‌بندی</label>
            <input
              type="text"
              value={totals.total_pack}
              readOnly
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
            <div className="border-b px-5 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">ویرایش کالا</h3>
              <button
                type="button"
                onClick={closeModal}
                className="px-3 py-1.5 rounded-lg border hover:bg-gray-50"
              >
                بستن
              </button>
            </div>

            <div className="px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Description */}
              <div className="md:col-span-2 flex flex-col gap-1.5">
                <label
                  htmlFor="modal_description"
                  className="text-sm text-gray-700"
                >
                  شرح کالا
                </label>
                <textarea
                  id="modal_description"
                  name="description"
                  value={modalItemData.description || ""}
                  onChange={handleModalChange}
                  placeholder="شرح کالا"
                  className={`${inputClass} min-h-[90px]`}
                />
              </div>

              {/* Quantity */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="modal_quantity"
                  className="text-sm text-gray-700"
                >
                  تعداد
                </label>
                <input
                  type="number"
                  id="modal_quantity"
                  name="quantity"
                  value={modalItemData.quantity ?? ""}
                  onChange={handleModalChange}
                  onWheel={(e) => e.target.blur()}
                  placeholder="تعداد"
                  className={inputClass}
                />
              </div>

              {/* Unit Price */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="modal_unit_price"
                  className="text-sm text-gray-700"
                >
                  قیمت واحد
                </label>
                <input
                  type="number"
                  id="modal_unit_price"
                  name="unit_price"
                  step="0.01"
                  value={modalItemData.unit_price ?? ""}
                  onChange={handleModalChange}
                  onWheel={(e) => e.target.blur()}
                  placeholder="قیمت واحد"
                  className={inputClass}
                />
              </div>

              {/* HS Code */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="modal_commodity_code"
                  className="text-sm text-gray-700"
                >
                  کد کالا (HS)
                </label>
                <input
                  type="text"
                  id="modal_commodity_code"
                  name="commodity_code"
                  inputMode="numeric"
                  dir="ltr"
                  maxLength={9}
                  value={modalItemData.commodity_code || ""}
                  onChange={(e) =>
                    handleSelectChange(
                      "commodity_code",
                      toLatinDigits(e.target.value).replace(/\D/g, "").slice(0, 9),
                    )
                  }
                  placeholder="حداکثر ۹ رقم"
                  className={inputClass}
                />
              </div>

              {/* Unit */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="modal_unit" className="text-sm text-gray-700">
                  واحد
                </label>
                <Select
                  inputId="modal_unit"
                  classNamePrefix="rs"
                  styles={selectStyles}
                  options={unitOptions}
                  value={
                    unitOptions.find((o) => o.value === modalItemData.unit) ||
                    null
                  }
                  onChange={handleModalUnitSelect}
                  placeholder="انتخاب واحد"
                />
              </div>

              {/* NW */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="modal_nw_kg" className="text-sm text-gray-700">
                  وزن خالص (کیلوگرم)
                </label>
                <input
                  type="number"
                  id="modal_nw_kg"
                  name="nw_kg"
                  step="0.01"
                  value={modalItemData.nw_kg ?? ""}
                  onChange={handleModalChange}
                  onWheel={(e) => e.target.blur()}
                  placeholder="وزن خالص"
                  className={inputClass}
                />
              </div>

              {/* GW */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="modal_gw_kg" className="text-sm text-gray-700">
                  وزن ناخالص (کیلوگرم)
                </label>
                <input
                  type="number"
                  id="modal_gw_kg"
                  name="gw_kg"
                  step="0.01"
                  value={modalItemData.gw_kg ?? ""}
                  onChange={handleModalChange}
                  onWheel={(e) => e.target.blur()}
                  placeholder="وزن ناخالص"
                  className={inputClass}
                />
              </div>

              {/* Pack */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="modal_pack" className="text-sm text-gray-700">
                  تعداد بسته‌بندی
                </label>
                <input
                  type="number"
                  id="modal_pack"
                  name="pack"
                  value={modalItemData.pack ?? ""}
                  onChange={handleModalChange}
                  onWheel={(e) => e.target.blur()}
                  placeholder="تعداد بسته‌بندی"
                  className={inputClass}
                />
              </div>

              {/* Origin (single select) */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="modal_origin" className="text-sm text-gray-700">
                  مبدأ
                </label>
                <Select
                  inputId="modal_origin"
                  classNamePrefix="rs"
                  styles={selectStyles}
                  options={countryOptions}
                  value={
                    countryOptions.find(
                      (o) => o.value === modalItemData.origin
                    ) || null
                  }
                  onChange={(opt) =>
                    handleSelectChange("origin", opt ? opt.value : "")
                  }
                  placeholder="انتخاب مبدأ کالا"
                />
              </div>
            </div>

            <div className="border-t px-5 py-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleModalSave}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                ذخیره
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 rounded-xl border hover:bg-gray-50 transition"
              >
                انصراف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
